import type { ThreadId } from "@cmmd-forge/contracts";

import { resolvePlanFollowUpSubmission } from "../../proposedPlan";
import {
  buildExpiredTerminalContextToastCopy,
  deriveComposerSendPreflight,
} from "../ChatView.logic";
import { toastManager } from "../ui/toast";
import type { ChatTurnSubmissionInput } from "./chatTurnSubmissionTypes";

type ComposerSendPreflight = ReturnType<typeof deriveComposerSendPreflight>;

export const IMAGE_ONLY_BOOTSTRAP_PROMPT =
  "[User attached one or more images without additional text. Respond using the conversation context and the attached image(s).]";

export function reportComposerPersistenceFailure(input: {
  readonly error: unknown;
  readonly threadId: ThreadId;
  readonly setThreadError: (threadId: ThreadId, message: string) => void;
}): void {
  const message =
    input.error instanceof Error
      ? input.error.message
      : "Forge could not save this message before sending it.";
  input.setThreadError(input.threadId, message);
  toastManager.add({
    type: "error",
    title: "Message was not sent",
    description: message,
  });
}

export async function handleNonSendComposerPreflight<ComposerTarget>(input: {
  readonly submission: ChatTurnSubmissionInput<ComposerTarget>;
  readonly preflight: Exclude<ComposerSendPreflight, { readonly type: "send-turn" }>;
  readonly trimmedPrompt: string;
}): Promise<void> {
  const { preflight, submission } = input;
  switch (preflight.type) {
    case "provider-disabled":
      // A null reason is a send held while the verdict is still loading:
      // blocked, silently, because the state is near-instant (forge#3394).
      if (preflight.reason !== null) {
        toastManager.add({
          type: "warning",
          title: "Provider disabled",
          description: preflight.reason,
        });
      }
      return;
    case "advance-pending-user-input":
      submission.onAdvanceActivePendingUserInput();
      return;
    case "submit-plan-follow-up": {
      const plan = submission.activeProposedPlan;
      if (!plan) return;
      const followUp = resolvePlanFollowUpSubmission({
        draftText: input.trimmedPrompt,
        planMarkdown: plan.planMarkdown,
      });
      submission.promptRef.current = "";
      submission.clearComposerDraftContent(submission.composerDraftTarget);
      submission.composerRef.current?.resetCursorState();
      await submission.onSubmitPlanFollowUp({
        text: followUp.text,
        interactionMode: followUp.interactionMode,
      });
      return;
    }
    case "apply-slash-command":
      if (preflight.command === "usage-limits") {
        if (submission.onOpenUsageLimits?.()) {
          submission.promptRef.current = "";
          submission.clearComposerDraftContent(submission.composerDraftTarget);
          submission.composerRef.current?.resetCursorState();
        }
        return;
      }
      submission.handleInteractionModeChange(preflight.command);
      submission.promptRef.current = "";
      submission.clearComposerDraftContent(submission.composerDraftTarget);
      submission.composerRef.current?.resetCursorState();
      return;
    case "empty-content":
      if (preflight.expiredTerminalContextCount > 0) {
        const copy = buildExpiredTerminalContextToastCopy(
          preflight.expiredTerminalContextCount,
          "empty",
        );
        toastManager.add({
          type: "warning",
          title: copy.title,
          description: copy.description,
        });
      }
      return;
    case "missing-project":
      submission.setThreadError(
        submission.activeThread?.id ?? null,
        "This thread's project is no longer available. Reconnect its runtime, or start a new chat.",
      );
  }
}
